import React, { useEffect, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Select from "react-select";
import { BlackExportIcon, BlackRefrechIcon } from "../../assets/icons";
import { useOutletContext } from "react-router-dom";
import FeedbackDetailsModal from "../../components/FeedbackDetailsModal";
import { useDispatch, useSelector } from "react-redux";
import {
  reqToGetroadmap,
  reqToUpdateFeedbackStatus,
} from "../../redux/slices.js/feedback/feedBackSlice";
import useExportCSV from "../../hooks/useExportCSV";
import { toast } from "react-toastify";

// Drag item types
const ItemTypes = {
  FEEDBACK_CARD: "feedbackCard",
};

// Draggable Card Component
const FeedbackCard = ({ item, columnTitle, onOpenModal }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ItemTypes.FEEDBACK_CARD,
    item: {
      id: item.id,
      sourceColumn: columnTitle,
      rawData: item.rawData,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <div
      ref={drag}
      onClick={() => onOpenModal(item)}
      className={`
        bg-white border border-[#F2F2F2] rounded-lg px-3 py-4 
        shadow-[0px_4px_32px_0px_#0000000D] flex flex-col gap-2 
        cursor-pointer transition-all hover:shadow-lg 
        hover:border-[#4285F4] 
        ${isDragging ? "opacity-50 scale-95" : "opacity-100"}
      `}
    >
      <div className="space-y-1">
        <p className="text-sub_small text-black mb-0">{item.title}</p>
        <p className="text-sub_span text-[#B4B4B4] mb-0">
          {item.source} . {item.time}
        </p>
      </div>
      <div className="flex gap-3 items-center mt-3">
        <span
          className={`px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body ${
            item.priority === "High"
              ? "bg-[#FFE4D7] text-[#F45C0C]"
              : item.priority === "Medium"
              ? "bg-[#FFFBD7] text-[#F4B40C]"
              : "bg-[#E4F7FF] text-[#0C8FF4]"
          }`}
        >
          {item.priority}
        </span>
        <span className="px-2 py-1 min-w-[57px] inline-block text-center rounded-full text-body bg-[#F6FFED] text-[#277600]">
          {item.votes} votes
        </span>
      </div>
    </div>
  );
};

// Droppable Column Component
const StatusColumn = ({ columnTitle, items, onOpenModal, onDropItem }) => {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ItemTypes.FEEDBACK_CARD,
    drop: (item) => onDropItem(item, columnTitle),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={drop}
      className={`
        bg-[#FAFAFA] py-4 px-3 rounded-lg border-[1px] 
        border-[#F2F2F2] flex flex-col space-y-4 md:min-h-[400px] max-h-[450px] overflow-auto
        transition-all
        ${isOver ? "bg-[#F0F8FF] border-[#4285F4]" : ""}
      `}
    >
      <h3 className="text-sub_span text-black flex items-center gap-2 border-b-[1px] border-[#F2F2F2] pb-4">
        {columnTitle}
        <span className="text-body-span text-white bg-[#4285F4] w-[20px] h-[20px] rounded-full flex items-center justify-center">
          {items.length}
        </span>
      </h3>

      {items.map((item) => (
        <FeedbackCard
          key={item.id}
          item={item}
          columnTitle={columnTitle}
          onOpenModal={onOpenModal}
        />
      ))}

      {/* Empty state drop zone */}
      {items.length === 0 && (
        <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4 text-center text-gray-400">
          <p>Drop here to move to {columnTitle}</p>
        </div>
      )}
    </div>
  );
};

// Main RoadMap Component
const RoadMap = () => {
  const { exportToCSV } = useExportCSV();

  const statuslist = useSelector((state) => state.feedbackslice.status);
  const types = useSelector((state) => state?.feedbackslice?.type);
  const { feedbackType, priority } = types || [];

  // Generate status options dynamically from statuslist
  const typeOptions = React.useMemo(() => {
    const options = (feedbackType || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Type",
    }));
    return [{ value: "all", label: "All Types" }, ...options];
  }, [feedbackType]);

  const statusOptions = React.useMemo(() => {
    const options = (statuslist || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Status",
    }));
    return [{ value: "all", label: "All Statuses" }, ...options];
  }, [statuslist]);

  const sortOptions = React.useMemo(() => {
    const prioritySortOptions = (priority || []).map((item) => ({
      value: item.id?.toString() || "",
      label: item.name || "Unknown Priority",
    }));
    return [{ value: "all", label: "All Priority" }, ...prioritySortOptions];
  }, [priority]);

  const dispatch = useDispatch();
  const { isSidebarOpen } = useOutletContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [typeFilter, setTypeFilter] = useState(
    typeOptions[0] || { value: "all", label: "All Types" }
  );
  const [statusFilter, setStatusFilter] = useState(
    statusOptions[0] || { value: "all", label: "All Statuses" }
  );
  const [sortOption, setSortOption] = useState(
    sortOptions[0] || { value: "all", label: "All Priority" }
  );

  const roadmapList = useSelector((state) => state.feedbackslice.roadmapList);
  const priorityOrder = useSelector(
    (state) => state.feedbackslice.type.priority
  );

  useEffect(() => {
    const filters = {
      ...(typeFilter.value !== "all" && { type: typeFilter.value }),
      ...(statusFilter.value !== "all" && { status: statusFilter.value }),
      ...(sortOption.value !== "newest" && { sort: sortOption.value }),
    };

    dispatch(
      reqToGetroadmap({
        data: filters,
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch, typeFilter, statusFilter, sortOption]);

  // Create status mapping from the selector
  const createStatusMapping = () => {
    if (!statuslist || statuslist.length === 0) {
      return {};
    }

    const mapping = {};
    statuslist.forEach((status) => {
      mapping[status.id] = status.name;
    });
    return mapping;
  };

  // Create reverse status mapping (name to id)
  const createReverseStatusMapping = () => {
    if (!statuslist || statuslist.length === 0) {
      return {};
    }

    const mapping = {};
    statuslist.forEach((status) => {
      mapping[status.name] = status.id;
    });
    return mapping;
  };

  // Transform roadmapList data to match your existing structure
  const transformRoadmapData = () => {
    if (!roadmapList || roadmapList.length === 0) {
      return {};
    }

    const statusMapping = createStatusMapping();

    // Create priority mapping from priorityOrder
    const priorityMapping = {};
    if (priorityOrder && priorityOrder.length > 0) {
      priorityOrder.forEach((priority) => {
        priorityMapping[priority.id] = priority.name;
      });
    }

    const groupedData = {};

    // Initialize all status groups from the dynamic status list
    if (statuslist && statuslist.length > 0) {
      statuslist.forEach((status) => {
        groupedData[status.name] = [];
      });
    }

    roadmapList.forEach((item) => {
      const status = statusMapping[item.status] || "New";
      const priority = priorityMapping[item.priority] || "Medium";

      // Calculate time ago
      const createdAt = new Date(item.createdAt);
      const now = new Date();
      const diffTime = Math.abs(now - createdAt);
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      let timeAgo = "";
      if (diffDays > 0) {
        timeAgo = `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
      } else {
        timeAgo = `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      }

      const transformedItem = {
        id: item.id,
        title: item.title,
        source: item.user?.name || "Unknown",
        time: timeAgo,
        priority: priority,
        votes: parseInt(item.votesCount) || 0,
        rawData: item, // Keep original data for modal
        currentStatus: status, // Keep current status for drag and drop
      };

      if (!groupedData[status]) {
        groupedData[status] = [];
      }
      groupedData[status].push(transformedItem);
    });

    return groupedData;
  };

  const data = transformRoadmapData();

  // Handle drop event
  const handleDropItem = (draggedItem, targetColumnTitle) => {
    if (draggedItem.sourceColumn === targetColumnTitle) {
      return; // Same column, no action needed
    }

    // Find the target status ID
    const reverseStatusMapping = createReverseStatusMapping();
    const targetStatusId = reverseStatusMapping[targetColumnTitle];

    if (!targetStatusId) {
      toast.error("Invalid status target");
      return;
    }

    // Call update status API using the correct service
    dispatch(
      reqToUpdateFeedbackStatus({
        id: draggedItem.id,
        data: parseInt(targetStatusId),
        onSuccess: () => {
          // Refresh the data after successful update
          handleRefresh();
        },
        onFailure: (error) => {
          console.error("Status update error:", error);
        },
      })
    );
  };

  const handleOpenModal = (item) => {
    setSelectedFeedback(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFeedback(null);
  };

  const handleRefresh = () => {
    const filters = {
      ...(typeFilter.value !== "all" && { type: typeFilter.value }),
      ...(statusFilter.value !== "all" && { status: statusFilter.value }),
      ...(sortOption.value !== "newest" && { sort: sortOption.value }),
    };

    dispatch(
      reqToGetroadmap({
        data: filters,
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  };

  const handleExport = () => {
    if (roadmapList && roadmapList.length > 0) {
      exportToCSV(
        roadmapList,
        typeOptions,
        statusOptions,
        sortOptions,
        `roadmap-export-${new Date().toISOString().split("T")[0]}.csv`
      );
    } else {
      toast("No data available to export", error);
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="space-y-5">
        <div className="flex items-center justify-between gap-3 sm:flex-nowrap flex-wrap">
          <div className="comman-form flex items-center gap-2 flex-wrap sm:w-fit w-full">
            <div className="form-group min-w-full sm:min-w-[186px]">
              <Select
                options={typeOptions}
                value={typeFilter}
                onChange={(option) => setTypeFilter(option || typeOptions[0])}
                placeholder="All Types"
                isClearable={false}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
            <div className="form-group min-w-full sm:min-w-[186px]">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(option) =>
                  setStatusFilter(option || statusOptions[0])
                }
                placeholder="All Statuses"
                isClearable={false}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
            <div className="form-group min-w-full sm:min-w-[186px]">
              <Select
                options={sortOptions}
                value={sortOption}
                onChange={(option) => setSortOption(option || sortOptions[0])}
                placeholder="Newest"
                isClearable={false}
                isSearchable={false}
                menuPlacement="auto"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap justify-end">
            <button
              type="button"
              className="normal-btn"
              onClick={handleRefresh}
            >
              {BlackRefrechIcon} Refresh
            </button>
            <button type="button" className="normal-btn" onClick={handleExport}>
              {BlackExportIcon} Export
            </button>
          </div>
        </div>

        <div className="pt-3 space-y-5">
          <div>
            <h2 className="text-span !font-medium text-black mb-0">
              Product Roadmap
            </h2>
            <p className="text-span text-dark mb-0">
              Drag and drop feedback items to change their status
            </p>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 ${
              isSidebarOpen ? "lg:grid-cols-2" : "lg:grid-cols-3"
            } xl:grid-cols-4 gap-3`}
          >
            {Object.entries(data).map(([columnTitle, items], idx) => (
              <StatusColumn
                key={idx}
                columnTitle={columnTitle}
                items={items}
                onOpenModal={handleOpenModal}
                onDropItem={handleDropItem}
              />
            ))}
          </div>
        </div>
      </div>
      <FeedbackDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        feedbackItem={selectedFeedback}
      />
    </DndProvider>
  );
};

export default RoadMap;
